import { NavigateFunction } from 'react-router-dom';

import { createAsyncThunk } from '@reduxjs/toolkit';

import { BinType } from 'commons/enums';
import { PRIMARY_BLUE, PRIMARY_RED } from 'commons/styles/colors';
import { STOItem } from 'commons/types';
import { defaultActionProcess } from 'commons/utils/defaultActionProcess';
import {
  addAssignedSTOPickItem,
  removeAssignedSTOPickItem,
  setAssignedSTOPickItem,
  setSTOItemPickActual,
  setSTOPickBinRecommendationInfo,
} from 'redux-stores/reducers/stoPickItemReducer';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';
import { STO_PICK_ROUTES } from 'routes/constants';
import {
  IPickingJobProductDetailRequest,
  IPickingJobProductDetailResponse,
} from 'services/apis/SwipeRxWmsApiPickJob/types';
import { swipeRxWmsApiStoPickItem } from 'services/apis/SwipeRxWmsApiStoPickItem';
import {
  ISTOItemParams,
  ISTOPickedItemRequest,
} from 'services/apis/SwipeRxWmsApiStoPickItem/types';

export const getSTOAssignedPickItems = createAsyncThunk(
  'stoPick/getSTOAssignedPickItems',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiStoPickItem.getSTOAssignedPickItems();
      dispatch(setAssignedSTOPickItem(data));
    }, dispatch);
    return undefined;
  },
);

export const getSTOPickItemsAction = createAsyncThunk(
  'stoPick/getSTOPickItemsAction',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiStoPickItem.getSTOPickItemList();
      dispatch(setAssignedSTOPickItem(data));
    }, dispatch);
    return undefined;
  },
);

export const actionSetAssignedSTOPickItem = createAsyncThunk(
  'stoPick/actionSetAssignedSTOPickItem',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiStoPickItem.assignSTOPickItem();
      dispatch(setAssignedSTOPickItem(data));
    }, dispatch);
    return undefined;
  },
);

export const actionAddAssignedSTOPickItem = createAsyncThunk(
  'stoPick/actionAddAssignedSTOPickItem',
  async (_, { dispatch }) => {
    await defaultActionProcess(async () => {
      const data = await swipeRxWmsApiStoPickItem.addSTOPickItem();
      dispatch(addAssignedSTOPickItem(data));
    }, dispatch);
    return undefined;
  },
);

export const actionRemoveAssignedSTOPickItem = createAsyncThunk(
  'stoPick/actionRemoveAssignedSTOPickItem',
  async (params: { stoNumber: string; skuCode: string }, { dispatch }) => {
    defaultActionProcess(async () => {
      const { stoNumber, skuCode } = params;
      const data = await swipeRxWmsApiStoPickItem.removeSTOPickItem({
        sto_number: stoNumber,
        sku_code: skuCode,
      });
      dispatch(removeAssignedSTOPickItem(data));
    }, dispatch);
    return undefined;
  },
);

export const actionStartSTOPickItem = createAsyncThunk(
  'stoPick/actionStartSTOPickItem',
  async (
    payload: { items: STOItem[]; navigate: NavigateFunction },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiStoPickItem.startPickingProcess(
        payload.items.map((item) => {
          return { sto_number: item.sto_number, sku_code: item.sku_code };
        }),
      );
      if (data !== 'ok') {
        dispatch(
          snackbarSetData({
            open: true,
            message: data,
            color: PRIMARY_BLUE,
          }),
        );
      }
      payload.navigate(STO_PICK_ROUTES.LIST);
    }, dispatch);
    return undefined;
  },
);

export const getSTOPickItemBinRecommendationInfo = createAsyncThunk(
  'stoPick/getSTOPickItemBinRecommendationInfo',
  async (params: { skuCode: string; binType: BinType }, { dispatch }) => {
    const { skuCode, binType } = params;
    defaultActionProcess(async () => {
      const result = await swipeRxWmsApiStoPickItem.getBinRecommendationInfo(
        skuCode,
        binType,
      );
      dispatch(setSTOPickBinRecommendationInfo(result));
    }, dispatch);
  },
);

export const completeSTOPickAction = createAsyncThunk(
  'stoPick/completeSTOPickAction',
  async (
    params: { items: ISTOItemParams[]; postAction: () => void },
    { dispatch },
  ) => {
    const { items, postAction } = params;
    defaultActionProcess(async () => {
      await swipeRxWmsApiStoPickItem.postCompletePicking({
        items,
      });
      postAction();
    }, dispatch);
  },
);

export const getSTOPickProductDetail = async (
  params: IPickingJobProductDetailRequest,
  dispatch: AppDispatch,
): Promise<IPickingJobProductDetailResponse | null> => {
  const response = await swipeRxWmsApiStoPickItem.getProductDetail(params);
  if (response && response.data) {
    return response.data;
  }
  if (response.error) {
    const errorMessage =
      response.error === true
        ? 'Failed to get product detail. Please make sure SKU, Bin, and Inventory Number is correct.'
        : response.error;
    dispatch(
      snackbarSetData({
        open: true,
        message: errorMessage,
        color: PRIMARY_RED,
      }),
    );
  }
  return null;
};

export const postSTOPickedItem = async (
  params: ISTOPickedItemRequest,
  dispatch: AppDispatch,
): Promise<void> => {
  const response = await swipeRxWmsApiStoPickItem.postPickedItem(params);
  if (!response) {
    dispatch(
      snackbarSetData({
        open: true,
        message:
          'Failed to get pick item. Please make sure SKU, Bin, Inventory Number, and Quantity is correct.',
        color: PRIMARY_RED,
      }),
    );
  } else {
    dispatch(
      setSTOItemPickActual({
        isRemove: false,
        item: params.item,
        itemIdx: params.item_idx,
      }),
    );
  }
};

export const postSTOPickRemoveBatch = async (
  params: ISTOPickedItemRequest,
  dispatch: AppDispatch,
): Promise<void> => {
  defaultActionProcess(
    async () => {
      await swipeRxWmsApiStoPickItem.postRemoveBatch({
        sku_code: params.sku_code,
        sto_number: params.sto_number,
        bin_number: params.item.bin,
        inventory_number: params.item.inventory_number,
      });

      dispatch(
        setSTOItemPickActual({
          isRemove: true,
          item: params.item,
          itemIdx: params.item_idx,
        }),
      );
    },
    dispatch,
    false,
  );
};

export const postSTOPickVerifyBasket = async (
  params: ISTOPickedItemRequest,
  dispatch: AppDispatch,
): Promise<boolean | undefined> => {
  return defaultActionProcess(
    async () => {
      await swipeRxWmsApiStoPickItem.postVerifyBasket({
        sku_code: params.sku_code,
        sto_number: params.sto_number,
        bin_number: params.item.bin,
        inventory_number: params.item.inventory_number,
        basket: params.item.basket,
      });

      dispatch(
        setSTOItemPickActual({
          isRemove: false,
          item: params.item,
          itemIdx: params.item_idx,
        }),
      );
      return true;
    },
    dispatch,
    false,
  );
};
